
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MapPin } from 'lucide-react';

// Define common cities for suggestions
const CITIES = [
  "Paris", "Tokyo", "New York", "London", "Rome", 
  "Barcelona", "Sydney", "Berlin", "Amsterdam", "Singapore",
  "Hong Kong", "Dubai", "Los Angeles", "San Francisco", "Toronto",
  "Seoul", "Bangkok", "Istanbul", "Vienna", "Prague"
];

interface CityFilterProps {
  value: string;
  onChange: (value: string) => void;
}

export const CityFilter = ({ value, onChange }: CityFilterProps) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Filter cities based on input
  const filteredCities = useMemo(() => {
    return CITIES.filter(city => 
      city.toLowerCase().includes(value.toLowerCase())
    ).slice(0, 10); // Limit to 10 results for better performance
  }, [value]);
  
  // Handle selecting a city from dropdown
  const handleCitySelect = (city: string) => {
    onChange(city);
    setFocused(false);
  };

  // Handle click on the input
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFocused(true);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <Label htmlFor="city-filter" className="text-sm font-medium mb-2 block">
        Filter by City
      </Label>
      <div className="relative" ref={inputRef}>
        <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
        <Input
          id="city-filter"
          placeholder="e.g. Paris, Tokyo..."
          className="pl-9"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onClick={handleClick}
        />
        
        {/* City Search Dropdown */}
        {focused && filteredCities.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            <ul className="py-1">
              {filteredCities.map((city) => (
                <li 
                  key={city}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleCitySelect(city)}
                >
                  {city}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
